import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { Message } from 'semantic-ui-react';

import LoadingDataEnhancer from './LoadingData';
import withPagination from './WithPagination';
import DateRangeSearch from '../views/Shared/DateRangeSearch';
import GuaranteeHistoryList from '../views/GuaranteeHistory/List';
import Export from '../views/GuaranteeHistory/Export';
import guaranteeHistoryQuery from '../graphql/guaranteeHistoryQuery';
import client from '../apollo';

import {
  setSearch,
  resetSearch,
  clearSearch,
} from '../data/searchField/actions';
import { setPage } from '../data/page/actions';
import { setRefreshSearch } from '../data/refreshSearch/actions';

const handleDateChangeRaw = event => {
  event.preventDefault();
};

const GuaranteeHistory = props => {
  const {
    data: { guaranteeHistoryReport, refetch },
    searchFields,
    refreshSearch,
    dispatchRefreshSearch,
    clearSearchFields,
    setSearchFields: setSearchFieldsAction,
    setPage: setPageAction,
    resetSearchFields,
  } = props;

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [completeGuaranteeHistoryReport, setCompleteGuaranteeHistoryReport] = useState([]);
  const [filename, setFilename] = useState('');
  const [fetchedCompleteRecords, setFetchedCompleteRecords] = useState(false);

  useEffect(() => {
    const searchButton = document.getElementById('searchButton');
    if (searchButton) {
      searchButton.style.background = '#13ae38';
      searchButton.style.color = '#fff';
      searchButton.style.marginRight = '10px';
    }
    if (refreshSearch) {
      dispatchRefreshSearch(false);
      refetch({
        startDate: null,
        endDate: null,
      });
      clearSearchFields({});
    }
  }, [refreshSearch, dispatchRefreshSearch, refetch, clearSearchFields]);

  useEffect(() => {
    if (
      !isEmpty(searchFields.startDate)
      && !isEmpty(searchFields.endDate)
      && !fetchedCompleteRecords
    ) {
      const start = moment(new Date(searchFields.startDate));
      const startDateMomentString = start.format('YY-MM-DD');
      const end = moment(new Date(searchFields.endDate));
      const endDateMomentString = end.format('YY-MM-DD');
      const datesString = `GuaranteeReport-${startDateMomentString}-${endDateMomentString}.csv`;
      client
        .query({
          query: guaranteeHistoryQuery,
          variables: {
            startDate: searchFields.startDate,
            endDate: searchFields.endDate,
            size: 0,
          },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          setCompleteGuaranteeHistoryReport(data.guaranteeHistoryReport);
          setFetchedCompleteRecords(true);
          setFilename(`${datesString}`);
        });
    }
  }, [searchFields, fetchedCompleteRecords]);

  const fetchGuaranteeHistory = useCallback(event => {
    const { startDate: sDate, endDate: eDate } = event.target;
    refetch({
      startDate: sDate.value,
      endDate: eDate.value,
    });
    setSearchFieldsAction({
      startDate: isEmpty(sDate.value)
        ? null
        : moment(new Date(sDate.value), 'YYYY-MM-DD'),
      endDate: isEmpty(eDate.value)
        ? null
        : moment(new Date(eDate.value), 'YYYY-MM-DD'),
    });
    setPageAction({});
  }, [refetch, setSearchFieldsAction, setPageAction]);

  const fetchCompleteRecords = useCallback(() => {
    setFetchedCompleteRecords(false);
  }, []);

  const onDateChangeHandler = useCallback((field, date) => {
    resetSearchFields(field);
    if (field === 'startDate') {
      setSearchFieldsAction({
        startDate: moment(new Date(date), 'YYYY-MM-DD'),
        endDate: null,
      });
      refetch({
        startDate: null,
        endDate: null,
      });
    }
    if (field === 'startDate') {
      setStartDate(moment(new Date(date), 'YYYY-MM-DD'));
    } else if (field === 'endDate') {
      setEndDate(moment(new Date(date), 'YYYY-MM-DD'));
    }
  }, [resetSearchFields, setSearchFieldsAction, refetch]);

  const clearDates = useCallback(() => {
    clearSearchFields({});
    setStartDate(null);
    setEndDate(null);
    refetch({
      startDate: null,
      endDate: null,
    });
  }, [clearSearchFields, refetch]);

  return (
    <div>
      <DateRangeSearch
        onDateChangeHandler={onDateChangeHandler}
        clearDates={clearDates}
        searchFields={searchFields}
        value={{ startDate, endDate }}
        onSearchHandler={fetchGuaranteeHistory}
        handleDateChangeRaw={handleDateChangeRaw}
      />
      <Export
        searchFields={searchFields}
        completeRecords={fetchCompleteRecords}
        completeGuaranteeHistoryReport={completeGuaranteeHistoryReport}
        filename={filename}
      />
      <br />
      {(!isEmpty(searchFields.startDate) || !isEmpty(startDate))
        && (!isEmpty(searchFields.endDate) || !isEmpty(endDate))
        && !isEmpty(guaranteeHistoryReport) && (
          <GuaranteeHistoryList guaranteeHistory={guaranteeHistoryReport} />
      )}
      {!isEmpty(searchFields) && isEmpty(guaranteeHistoryReport) && (
        <Message>No Records found</Message>
      )}
      {isEmpty(searchFields) && isEmpty(guaranteeHistoryReport) && (
        <Message negative>
          Please enter start date and end date and press the SEARCH button to
          proceed
        </Message>
      )}
    </div>
  );
};

GuaranteeHistory.propTypes = {
  data: PropTypes.shape({
    guaranteeHistoryReport: PropTypes.array,
    refetch: PropTypes.func,
  }).isRequired,
  searchFields: PropTypes.shape({
    startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    endDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  }).isRequired,
  refreshSearch: PropTypes.bool,
  dispatchRefreshSearch: PropTypes.func.isRequired,
  clearSearchFields: PropTypes.func.isRequired,
  setSearchFields: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  resetSearchFields: PropTypes.func.isRequired,
};

GuaranteeHistory.defaultProps = {
  refreshSearch: false,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  setPage: () => {
    dispatch(setPage());
  },
  clearSearchFields: () => {
    dispatch(clearSearch());
  },
  resetSearchFields: field => {
    dispatch(resetSearch(field));
  },
  dispatchRefreshSearch: value => {
    dispatch(setRefreshSearch(value));
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields, refreshSearch },
  } = state;
  return {
    searchFields,
    refreshSearch,
  };
};

export default compose(
  withRouter,
  withPagination(guaranteeHistoryQuery, 'guaranteeHistoryReport'),
  LoadingDataEnhancer,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(GuaranteeHistory);
